/**
 * Language Hooks
 *
 * Custom React hooks for language functionality.
 */

import { useEffect } from 'react';
import { useLanguageStore } from '../store/languageStore.js';
import { useUserStore } from '../store/userStore.js';
import type { Language, LanguageCode } from '../services/languageApi.js';

/**
 * Hook to get the current language
 */
export function useCurrentLanguage(): LanguageCode | null {
  const currentLanguage = useLanguageStore((state) => state.currentLanguage);
  return currentLanguage;
}

/**
 * Hook to get all available languages
 * Automatically loads languages on mount if not already loaded
 */
export function useAvailableLanguages(): {
  languages: Language[];
  isLoading: boolean;
  error: string | null;
} {
  const { availableLanguages, isLoading, error, loadAvailableLanguages } = useLanguageStore();

  useEffect(() => {
    // Load languages if not already loaded
    if (availableLanguages.length === 0 && !isLoading && !error) {
      loadAvailableLanguages().catch(console.error);
    }
  }, [availableLanguages.length, isLoading, error, loadAvailableLanguages]);

  return {
    languages: availableLanguages,
    isLoading,
    error,
  };
}

/**
 * Hook to switch languages
 */
export function useSwitchLanguage(): {
  switchLanguage: (languageCode: LanguageCode) => Promise<void>;
  isLoading: boolean;
  error: string | null;
} {
  const user = useUserStore((state) => state.user);
  const { setUserLanguage, isLoading, error } = useLanguageStore();

  const switchLanguage = async (languageCode: LanguageCode) => {
    if (!user?.id) {
      throw new Error('User must be logged in to switch languages');
    }

    await setUserLanguage(user.id, languageCode);
  };

  return {
    switchLanguage,
    isLoading,
    error,
  };
}

/**
 * Hook to get language progress for the current user
 */
export function useLanguageProgress(): {
  progress: Record<string, any>;
  currentLanguageProgress: any | null;
  isLoading: boolean;
  error: string | null;
  refreshProgress: () => Promise<void>;
} {
  const user = useUserStore((state) => state.user);
  const {
    languageProgress,
    currentLanguage,
    isLoading,
    error,
    loadUserLanguageProgress,
  } = useLanguageStore();

  const refreshProgress = async () => {
    if (!user?.id) {
      throw new Error('User must be logged in to load progress');
    }
    await loadUserLanguageProgress(user.id);
  };

  const currentLanguageProgress = currentLanguage
    ? languageProgress[currentLanguage]
    : null;

  return {
    progress: languageProgress,
    currentLanguageProgress,
    isLoading,
    error,
    refreshProgress,
  };
}

/**
 * Hook to initialize user language preference on app load
 */
export function useInitializeLanguage(): {
  isLoading: boolean;
  error: string | null;
} {
  const user = useUserStore((state) => state.user);
  const { currentLanguage, isLoading, error, loadUserLanguagePreference } = useLanguageStore();

  useEffect(() => {
    // Load user language preference if user is logged in and not already loaded
    if (user?.id && !currentLanguage && !isLoading && !error) {
      loadUserLanguagePreference(user.id).catch(console.error);
    }
  }, [user?.id, currentLanguage, isLoading, error, loadUserLanguagePreference]);

  return {
    isLoading,
    error,
  };
}

/**
 * Hook to get a specific language by code
 */
export function useLanguageByCode(code: LanguageCode): Language | null {
  const { languages } = useAvailableLanguages();
  return languages.find((lang) => lang.code === code) || null;
}

/**
 * Convenience hook that combines current language code with full language object
 */
export function useLanguage(): {
  currentLanguage: Language | null;
  currentLanguageCode: LanguageCode | null;
  availableLanguages: Language[];
  isLoading: boolean;
  error: string | null;
} {
  const currentLanguageCode = useCurrentLanguage();
  const { languages, isLoading, error } = useAvailableLanguages();
  const currentLanguage = currentLanguageCode
    ? languages.find((lang) => lang.code === currentLanguageCode) || null
    : null;

  return {
    currentLanguage,
    currentLanguageCode,
    availableLanguages: languages,
    isLoading,
    error,
  };
}
